import * as React from 'react';
import RibbonButton from './buttons/RibbonButton';
import RibbonPlugin from '../plugin/RibbonPlugin';
import RibbonViewState from '../store/schema/RibbonViewState';
import RibbonButtonView from './RibbonButtonView';
import getButtons from '../utils/getButtons';
import ensureUndoVisible from '../utils/ensureUndoVisible';
import more from './buttons/more';
import updateRibbonState, { updateSecondClassState } from '../actions/updateRibbonState';
import updateRibbonWidth from '../actions/updateRibbonWidth';
import { Callout, DirectionalHint } from 'office-ui-fabric-react/lib/Callout';
import { logUsage } from 'owa-analytics';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { isBrowserFirefox } from 'owa-user-agent';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getStyleBasedFormatState } from 'roosterjs-editor-api';

const styles = require('./Ribbon.scss');
const classNames = require('classnames/bind').bind(styles);

export interface RibbonProps {
    /**
     * Ribbon Plugin
     */
    ribbonPlugin: RibbonPlugin;

    /**
     * Ribbon ViewState
     */
    viewState: RibbonViewState;

    /**
     * true if the ribbon should render in right-to-left mode
     */
    isRtl: boolean;

    /**
     * Whether we want to skip the auto resize when initializing
     */
    skipInitialResize?: boolean;

    /**
     * Buttons
     */
    buttons?: RibbonButton[];

    /**
     * Callback to invoke when dropdown is dismissed
     */
    onDropDownDismissed?: () => void;

    /**
     * Whether set focus to the botton before drop down is shown
     * By default, we will set the focus to editor
     */
    focusToButtonBeforeDropDown?: boolean;

    /**
     * CSS class name to apply to the outer div
     */
    className?: string;

    /**
     * Dark mode flag to control styling.
     */
    isDarkMode?: boolean;

    /**
     * Disable UI
     */
    isDisabled?: boolean;
}

export interface RibbonState {
    dropDownHistoryStack: RibbonButton[];
}

@observer
export default class Ribbon extends React.Component<RibbonProps, RibbonState> {
    private buttonElements: { [key: string]: HTMLElement } = {};
    private rootDiv: HTMLElement;

    constructor(props: RibbonProps) {
        super(props);
        this.state = {
            dropDownHistoryStack: [],
        };
    }

    componentDidMount() {
        if (this.rootDiv && !this.props.skipInitialResize) {
            updateRibbonWidth(this.props.viewState, this.rootDiv.clientWidth);
        }
    }

    // Handle the case where the same Ribbon is reused with a new ribbon view state
    // (e.g. one where the visibleItemCount does not match the dom on this ribbon)
    componentDidUpdate(prevProps) {
        if (
            this.rootDiv &&
            !this.props.skipInitialResize &&
            this.props.viewState !== prevProps.viewState
        ) {
            updateRibbonWidth(this.props.viewState, this.rootDiv.clientWidth);
        }
    }

    @computed
    get visibleItems(): RibbonButton[] {
        let {
            viewState: { visibleItemCount },
            buttons,
        } = this.props;
        let allButtons = buttons || getButtons();
        visibleItemCount = visibleItemCount || allButtons.length;
        return visibleItemCount >= allButtons.length
            ? allButtons.slice()
            : allButtons.slice(0, visibleItemCount - 1);
    }

    @computed
    get overflowItems(): RibbonButton[] {
        const { buttons } = this.props;
        let allButtons = buttons || getButtons();
        return allButtons.slice(this.visibleItems.length, allButtons.length);
    }

    render() {
        let { className } = this.props;
        // Force undo to the end of visibleItems if it is in overflowItems
        if (isFeatureEnabled('cmp-ribbonStickyUndo')) {
            ensureUndoVisible(this.visibleItems, this.overflowItems);
        }

        let visibleItemsWithOverflow = this.overflowItems.length
            ? this.visibleItems.concat([more])
            : this.visibleItems;

        const { dropDownHistoryStack } = this.state;
        const dropDown = dropDownHistoryStack[dropDownHistoryStack.length - 1];
        let showMoreDropDown = dropDownHistoryStack.indexOf(more) >= 0;

        return (
            <div ref={ref => (this.rootDiv = ref)} className={className}>
                <FocusZone
                    role="toolbar"
                    aria-label="Formatting options."
                    className={styles.ribbon}
                    direction={FocusZoneDirection.horizontal}>
                    {visibleItemsWithOverflow.map(this.renderRibbonButton)}
                    {showMoreDropDown && this.renderMoreDropDown()}
                    {dropDown && dropDown != more && this.renderDropdown(dropDown)}
                </FocusZone>
            </div>
        );
    }

    private renderMoreDropDown(): JSX.Element {
        const { dropDownHistoryStack } = this.state;
        const dropDown = dropDownHistoryStack[dropDownHistoryStack.length - 1];
        return (
            <Callout
                className={classNames(styles.affordanceCallout, {
                    needFallback: !this.props.isDarkMode,
                })}
                onDismiss={dropDown == more ? this.onDismissDropDown : null}
                gapSpace={12}
                target={this.buttonElements[more.key]}
                isBeakVisible={false}
                setInitialFocus={true}
                directionalHint={DirectionalHint.topCenter}>
                <FocusZone
                    className={styles.affordanceCalloutButtonContainer}
                    role="toolbar"
                    aria-label="Formatting options."
                    direction={FocusZoneDirection.bidirectional}>
                    {this.overflowItems.map(this.renderRibbonButton)}
                </FocusZone>
            </Callout>
        );
    }

    private renderDropdown(dropDown: RibbonButton): JSX.Element {
        const DropDown = dropDown.createDropDownCallback;
        return DropDown ? (
            <DropDown
                target={this.buttonElements[dropDown.key]}
                plugin={this.props.ribbonPlugin}
                dismiss={this.onDismissDropDown}
                logUsage={this.onLogUsage}
            />
        ) : null;
    }

    private renderRibbonButton = (ribbonButton: RibbonButton) => {
        const BoundRibbonButton = this.BoundRibbonButton;
        return <BoundRibbonButton key={ribbonButton.key} ribbonButton={ribbonButton} />;
    };

    /**
     * A ribbon button view with callbacks bound to this component
     */
    private BoundRibbonButton = React.memo(({ ribbonButton }: { ribbonButton: RibbonButton }) => {
        const onClick = React.useCallback(() => this.onRibbonButton(ribbonButton), []);
        const bindRef = React.useCallback(ref => {
            this.buttonElements[ribbonButton.key] = ref;
        }, []);
        const isRibbonDisabled = React.useCallback(() => this.isRibbonDisabled(), []);
        const { dropDownHistoryStack } = this.state;
        const dropDown = dropDownHistoryStack[dropDownHistoryStack.length - 1];
        return (
            <RibbonButtonView
                key={ribbonButton.key}
                ribbonButton={ribbonButton}
                formatViewState={this.props.viewState.formatViewState}
                isDropDownOpen={dropDown == ribbonButton}
                onClick={onClick}
                containerRef={bindRef}
                isRtl={this.props.isRtl}
                isDarkMode={this.props.isDarkMode}
                isRibbonDisabled={isRibbonDisabled}
            />
        );
    });

    private isRibbonDisabled() {
        return !!this.props.isDisabled;
    }

    private onRibbonButton(button: RibbonButton) {
        let { viewState, ribbonPlugin, focusToButtonBeforeDropDown } = this.props;
        let editor = ribbonPlugin.getEditor();
        if (editor) {
            // Handle click event of the button
            if (button.createDropDownCallback) {
                updateSecondClassState(viewState.formatViewState, getStyleBasedFormatState(editor));
                if (focusToButtonBeforeDropDown) {
                    let element = this.buttonElements[button.key];

                    // Focus to the button element so that once the dropdown is closed, fabric can
                    // focus back to the button.
                    if (element) {
                        element.focus();
                    }
                } else {
                    editor.focus();
                }

                const { dropDownHistoryStack } = this.state;
                if (dropDownHistoryStack.indexOf(button) >= 0) {
                    this.clearStack(button);
                } else {
                    let parentButton = this.getParentButtonInStack(button);
                    this.clearStack(parentButton);

                    if (parentButton) {
                        dropDownHistoryStack.push(parentButton);
                    }

                    dropDownHistoryStack.push(button);
                }
                this.setState({
                    dropDownHistoryStack,
                });
            } else if (button.onExecute) {
                logUsage('EditorRibbonUsage', [button.key]);

                // 35341 React: In Firefox, cursor disappears from editor if you get Discard message, click Cancel, and continue typing in message.
                // 40312 Inserting a link via the format bar places the link at the beginning of a message rather than linking the selected text. (Using Chrome on a Mac)
                //
                // 35341 only repro in Firefox, To fix it, we can't focus into editor here otherwise cursor will be lost after dialog is closed
                // 40312 doesn't repro in Firefox. And to fix it, we need to focus here.
                // To make everything works, we add check to Firefox here, and only focus when browser is not Firefox, so everybody happies.
                if (!isBrowserFirefox()) {
                    editor.focus();
                }

                button.onExecute(ribbonPlugin, this.onLogUsage);
                updateRibbonState(
                    ribbonPlugin,
                    viewState,
                    false /*updateContextualFormatBar*/,
                    false /*delay*/
                );
            }
        }
    }

    private onDismissDropDown = () => {
        let { dropDownHistoryStack } = this.state;
        dropDownHistoryStack.pop();
        this.setState({
            dropDownHistoryStack,
        });

        if (this.props.onDropDownDismissed) {
            this.props.onDropDownDismissed();
        }
    };

    private onLogUsage(category: string, value: any) {
        logUsage('EditorRibbonUsage' + category, [JSON.stringify(value)]);
    }

    private clearStack(fromButton?: RibbonButton) {
        const { dropDownHistoryStack } = this.state;
        let popElement = dropDownHistoryStack.pop();
        while (popElement && popElement != fromButton) {
            popElement = dropDownHistoryStack.pop();
        }
    }

    private getParentButtonInStack(child: RibbonButton): RibbonButton {
        if (this.overflowItems.indexOf(child) >= 0) {
            return more;
        }
        return null;
    }
}
